import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../../../environments/environment';
import { UserService } from './user.service';

type TawkApp = 'public' | 'customer' | 'booster' | 'admin';

type TawkUser = {
  nickname?: string;
  email?: string;
  role?: string;
  tawkHash?: string;
  lastOrderDate?: string;
};

declare global {
  interface Window {
    Tawk_API?: any;
  }
}

@Injectable()
export class TawkChatService {
  private readonly isBrowser: boolean;
  private booted = false;
  private app: TawkApp;
  private pendingUser: TawkUser | null = null;

  constructor(
    @Inject(PLATFORM_ID) platformId: string,
    private userService: UserService
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  // The widget script itself is injected from each app's index.html. This
  // service only feeds the logged-in user into the already-loaded widget,
  // mirroring how the Crisp integration identified users.
  public boot(app: TawkApp): void {
    if (this.booted || !this.shouldLoad(app)) {
      return;
    }
    this.booted = true;
    this.app = app;

    // Identify whoever is already known, then keep in sync with auth changes.
    const current = this.userService.getUserSync();
    if (current && current.email) {
      this.identify(current);
    }

    this.userService.userUpdated.subscribe((user: TawkUser) => {
      if (user && user.email) {
        this.identify(user);
      } else {
        this.reset();
      }
    });
  }

  public identify(user: TawkUser): void {
    if (!this.isBrowser || !user) {
      return;
    }
    this.pendingUser = user;
    this.whenReady(() => {
      const attributes: { [key: string]: string } = {};
      if (this.pendingUser.nickname) {
        attributes.name = this.pendingUser.nickname;
      }
      if (this.pendingUser.email) {
        attributes.email = this.pendingUser.email;
      }
      if (this.pendingUser.role) {
        attributes.role = this.pendingUser.role;
      }
      // Customers only; attribute keys must be alphanumeric + dash.
      if (this.pendingUser.lastOrderDate) {
        attributes['last-order-date'] = this.pendingUser.lastOrderDate;
      }
      // Only present when secure mode is enabled and the API key is set on the
      // backend. tawk.to ignores/rejects a stale or missing hash gracefully.
      if (this.pendingUser.tawkHash) {
        attributes.hash = this.pendingUser.tawkHash;
      }

      window.Tawk_API.setAttributes(attributes, (error: any) => {
        if (error) {
          console.warn('[tawk] setAttributes failed', error);
        }
      });
    });
  }

  public reset(): void {
    if (!this.isBrowser) {
      return;
    }
    this.pendingUser = null;
    const api = window.Tawk_API;
    if (api && typeof api.logout === 'function') {
      api.logout(() => {});
    }
  }

  private shouldLoad(app: TawkApp): boolean {
    if (!this.isBrowser) {
      return false;
    }
    const cfg = (environment as any).tawk;
    return !!(cfg && cfg.enabled && cfg.apps && cfg.apps[app]);
  }

  // setAttributes is only valid once the widget has loaded. Run the callback
  // immediately if it is ready, otherwise defer to the onLoad hook.
  private whenReady(fn: () => void): void {
    const api = (window.Tawk_API = window.Tawk_API || {});
    if (typeof api.setAttributes === 'function') {
      fn();
      return;
    }
    const previousOnLoad = api.onLoad;
    api.onLoad = () => {
      if (typeof previousOnLoad === 'function') {
        previousOnLoad();
      }
      fn();
    };
  }
}
